/**
 * A federated partner in the record panel: its state, its credential's
 * deadline, and Open to the partner page where rotation and revocation live.
 */
import { useQuery } from '@tanstack/react-query';

import { listFederatedPartners } from '@optum/api-client/endpoints/federated-partners';

import type { RecordRef } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordListError,
  RecordMissing,
  RecordOpen,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { LoadingBlock } from '../../ui/AsyncState.js';
import { absoluteDate, shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { expiryStatus } from '../../lib/token-expiry.js';
import { IconTile, Typography } from '../../ui/finixt.js';
import { StatusBadge } from '../../ui/StatusBadge.js';

export function PartnerRecord({ record }: { record: RecordRef }) {
  const partners = useQuery({
    queryKey: qk.federatedPartners(),
    queryFn: () => listFederatedPartners(),
  });
  if (partners.isPending) return <LoadingBlock />;
  if (partners.isError) {
    return (
      <RecordListError
        message={partners.error instanceof Error ? partners.error.message : 'Failed to load'}
      />
    );
  }
  const p = partners.data.find((row) => row.id === record.id);
  if (!p) return <RecordMissing record={record} />;
  const expiry = expiryStatus(p.expires_at);

  return (
    <RecordColumn>
      <RecordIdentity
        avatar={<IconTile icon="hub" size={40} tint="var(--info-tint)" color="var(--info)" />}
        eyebrow={
          <>
            <Typography variant="eyebrow">Partner</Typography>
            <StatusBadge status={p.status} vocabulary="federatedPartner" />
          </>
        }
        name={p.name}
        subtitle={p.description ?? p.idp_name}
      />
      <RecordSection label="Facts">
        <RecordFacts
          items={[
            { label: 'ID', value: shortId(p.id) },
            { label: 'Identity provider', value: p.idp_name },
            { label: 'Contact', value: p.contact_email ?? '—', mono: false },
            { label: 'Requested', value: absoluteDate(p.created_at) },
            { label: 'Approved', value: p.approved_at ? absoluteDate(p.approved_at) : '—' },
            {
              label: 'Token expires',
              value: p.expires_at ? `${absoluteDate(p.expires_at)} · ${expiry.state}` : 'undated',
            },
            { label: 'Rotated', value: p.rotated_at ? absoluteDate(p.rotated_at) : 'never' },
          ]}
        />
      </RecordSection>
      {p.rejected_reason ? (
        <RecordSection label="Rejected because">
          <Typography variant="body-muted">{p.rejected_reason}</Typography>
        </RecordSection>
      ) : null}
      <RecordActions>
        <RecordOpen record={record} />
      </RecordActions>
    </RecordColumn>
  );
}
